import React, { useState, useEffect } from 'react';
import { Button, Chip, Divider, IconButton, Dialog, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useNavigate } from 'react-router-dom';
import {
  ContentClustersDataV2,
  ContentClusterItemV2
} from '../../types/content-strategy-v2.type';
import GenerateBlogGuidanceModal from './GenerateBlogGuidanceModal';
import { useContentStrategyActions } from '../hooks/useContentStrategyActions';

interface ContentClustersProps {
  data?: ContentClustersDataV2;
  page?: number;
  onPageChange?: (newPage: number) => void;
  // fallback for legacy array prop
  contentClusters?: ContentClusterItemV2[];
  isLoading?: boolean;
}

export const ContentClusters: React.FC<ContentClustersProps> = ({
  data,
  page = 1,
  onPageChange,
  contentClusters,
  isLoading = false
}) => {
  const navigate = useNavigate();
  const { generateBlogFromBlueprintMutation, generateClusterBlueprintsMutation } =
    useContentStrategyActions();

  const [selectedCluster, setSelectedCluster] =
    useState<ContentClusterItemV2 | null>(null);
  const [activeBlueprint, setActiveBlueprint] = useState<any | null>(null);

  const items = data?.items || contentClusters || [];
  const total = data?.total || items.length;
  const limit = data?.limit || 3;
  const totalPages = Math.ceil(total / limit) || 1;

  useEffect(() => {
    if (selectedCluster) {
      const updated = items.find((item) => item.id === selectedCluster.id);
      if (updated) {
        setSelectedCluster(updated);
      }
    }
  }, [items]);

  const handleGenerateBlog = (blueprintId: string, suggestion?: string) => {
    generateBlogFromBlueprintMutation.mutate(
      { blueprintId, suggestion },
      {
        onSuccess: (res: any) => {
          setActiveBlueprint(null);
          const runId = res?.blog_run_id || res?.run_id;
          if (runId) {
            navigate(`/orbit-ai-agent/blog-post-agent?runId=${runId}`);
          } else {
            navigate('/orbit-ai-agent/blog-post-agent');
          }
        }
      }
    );
  };

  const handleGenerateClusterBlueprints = (cluster: ContentClusterItemV2) => {
    const customEndpoint = cluster.actions?.generate_cluster_blueprints?.endpoint;
    generateClusterBlueprintsMutation.mutate({
      clusterId: cluster.id,
      customEndpoint
    });
  };

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 z-50 flex items-center justify-center backdrop-blur-[1px] rounded-xl">
          <CircularProgress size={40} sx={{ color: '#120242' }} />
        </div>
      )}
      {/* Header */}
      <div className="flex justify-between items-center px-6 pb-4">
        <p className="text-base font-medium text-[#475467]">
          Content Clusters
        </p>

        {total > 0 && onPageChange && (
          <div className="flex items-center gap-2">
            <p className="text-primary text-base font-medium">
              {page}/{totalPages}
            </p>

            <div className="flex items-center">
              <IconButton
                size="small"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
              >
                <NavigateNextIcon />
              </IconButton>
            </div>
          </div>
        )}
      </div>

      <Divider />

      {items.length > 0 ? (
        items.map((item, index) => {
          const blueprintCount = item.blueprints?.length || 0;

          return (
            <div key={item.id || index}>
              <div className="px-6 py-4 flex justify-between items-center">
                {/* LEFT */}
                <div className="flex-1">
                  {/* TOP */}
                  <div className="flex items-center gap-4 mb-3 flex-wrap">
                    <p className="font-semibold text-base text-[#1D2939]">
                      {item.cluster_name}
                    </p>

                    {blueprintCount > 0 && (
                      <Chip
                        label={
                          <span className="text-xs font-medium">
                            ✓ {blueprintCount} blueprints ready
                          </span>
                        }
                        size="small"
                        className="!bg-[#E6F9F7] !text-[#0D6F66] !font-medium"
                      />
                    )}
                  </div>

                  <div className="flex gap-6 flex-wrap mb-3">
                    <Chip
                      label={
                        <span className="text-xs font-medium">
                          {`Pillar: ${item.pillar_topic}`}
                        </span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />

                    <Chip
                      label={
                        <span className="text-xs font-medium">
                          {`Est. Traffic: ${
                            item.estimated_cluster_traffic?.toLocaleString() || 0
                          }/month`}
                        </span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />

                    <Chip
                      label={
                        <span className="text-xs font-medium">
                          {`Supporting Topics: ${
                            item.supporting_topics?.length || 0
                          }`}
                        </span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />
                  </div>

                  {/* Supporting Topics */}
                  {item.supporting_topics &&
                    item.supporting_topics.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {item.supporting_topics
                          .slice(0, 4)
                          .map((topic, tIdx) => (
                            <Chip
                              key={tIdx}
                              label={
                                <span className="text-xs font-medium">
                                  {topic}
                                </span>
                              }
                              size="small"
                              className="!bg-[#F2F4F7]"
                            />
                          ))}
                        {item.supporting_topics.length > 4 && (
                          <Chip
                            label={
                              <span className="text-xs font-medium">
                                +{item.supporting_topics.length - 4} more
                              </span>
                            }
                            size="small"
                            className="!bg-[#F2F4F7]"
                          />
                        )}
                      </div>
                    )}
                </div>

                {/* RIGHT */}
                <div className="flex gap-4 ml-8">
                  <Button
                    variant="outlined"
                    className="px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
                    onClick={() => setSelectedCluster(item)}
                  >
                    Details
                  </Button>
                </div>
              </div>

              {index !== items.length - 1 && <Divider />}
            </div>
          );
        })
      ) : (
        <div className="px-6 py-8 text-center text-[#667085] text-sm font-normal">
          No data found
        </div>
      )}

      {/* Cluster Details Dialog */}
      <Dialog
        open={!!selectedCluster}
        onClose={() => setSelectedCluster(null)}
        disableScrollLock
      >
        {selectedCluster && (
          <div className="p-6 min-w-[600px] max-w-[600px] relative">
            {/* Header */}
            <p className="text-primary font-semibold text-xl mb-4 pr-10">
              {selectedCluster.cluster_name}
            </p>

            <div className="flex flex-col gap-4 mb-6">
              {/* Pillar Topic */}
              <div>
                <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                  Pillar Topic
                </h4>
                <p className="text-sm text-[#344054] leading-relaxed">
                  {selectedCluster.pillar_topic}
                </p>
              </div>

              {/* Supporting Topics */}
              <div>
                <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                  Supporting Topics ({selectedCluster.supporting_topics?.length || 0})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedCluster.supporting_topics?.map((topic, idx) => (
                    <Chip
                      key={idx}
                      label={
                        <span className="text-xs font-medium">{topic}</span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />
                  ))}
                </div>
              </div>

              {/* Blueprints */}
              {selectedCluster.blueprints &&
              selectedCluster.blueprints.length > 0 ? (
                <div>
                  <h4 className="font-semibold text-sm text-[#475467] mb-1.5">
                    Blueprints ({selectedCluster.blueprints.length})
                  </h4>
                  <div className="flex flex-col gap-3">
                    {selectedCluster.blueprints.map(
                      (bp: any, bpIdx: number) => (
                        <div
                          key={bp._id || bpIdx}
                          className="p-4 bg-[#F9FAFB] rounded-lg border border-[#EAECF0]"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <p className="font-semibold text-sm text-[#1D2939]">
                              {bp.title || bp.topic}
                            </p>
                            <Chip
                              label={
                                <span className="text-[10px] font-semibold uppercase">
                                  {bp.role || (bpIdx === 0 ? 'Pillar' : 'Supporting')}
                                </span>
                              }
                              size="small"
                              className="!bg-[#E6F9F7] !text-[#0D6F66]"
                            />
                          </div>

                          {bp.synopsis && (
                            <p className="text-xs text-[#667085] leading-relaxed mb-3">
                              {bp.synopsis}
                            </p>
                          )}
                          {/* TODO */}
                          {/* <div className="flex justify-end">
                            <Button
                              variant="contained"
                              className="!bg-[#120242] !text-white !normal-case !font-semibold !text-xs"
                              onClick={() => {
                                setSelectedCluster(null);
                                setActiveBlueprint(bp);
                              }}
                            >
                              Generate Blog →
                            </Button>
                          </div> */}
                        </div>
                      )
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-2 flex flex-col items-center justify-center p-6 bg-[#F9FAFB] rounded-lg border border-[#EAECF0] text-center">
                  <p className="text-sm text-[#475467] mb-3 font-medium">
                    No blueprints generated yet for this cluster.
                  </p>
                  <Button
                    variant="contained"
                    disabled={generateClusterBlueprintsMutation.isPending}
                    onClick={() => handleGenerateClusterBlueprints(selectedCluster)}
                    className="!bg-[#120242] !text-white !normal-case !font-semibold !text-sm px-4 py-2"
                    startIcon={
                      generateClusterBlueprintsMutation.isPending ? (
                        <CircularProgress size={16} sx={{ color: 'white' }} />
                      ) : null
                    }
                  >
                    {generateClusterBlueprintsMutation.isPending
                      ? 'Generating Blueprints...'
                      : 'Generate blue print'}
                  </Button>
                </div>
              )}
            </div>

            {/* Absolute Close Icon */}
            <div
              aria-label="dialog-close-btn"
              className="absolute top-0 right-0"
            >
              <IconButton
                aria-label="close"
                className="hover:rounded-lg"
                sx={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  zIndex: 2,
                  height: '40px',
                  padding: '8px',
                  paddingRight: '8px'
                }}
                onClick={() => setSelectedCluster(null)}
              >
                <CloseIcon />
              </IconButton>
            </div>
          </div>
        )}
      </Dialog>

      {/* Blog Generation Guidance Modal */}
      <GenerateBlogGuidanceModal
        open={Boolean(activeBlueprint)}
        onClose={() => setActiveBlueprint(null)}
        blueprint={activeBlueprint}
        onGenerate={handleGenerateBlog}
        isGenerating={generateBlogFromBlueprintMutation.isPending}
      />
    </div>
  );
};

export default ContentClusters;
